import React, { useContext } from 'react'

import { Link } from '@vividseats/vivid-ui-kit'
import CardWithImageAndFooter from '@vividseats/vivid-ui-kit/dist/components/card/components/card-with-image-and-footer'
import NextLink from 'next/link'

import productionDetailsContext from '@/context/production-details'
import { normalizeSecureImages } from '@/context/utils/image-path'
import { Production } from '@/types/app-types'
import {
    dateAndTimeFromLocalString,
    formatDateWithConditionalYear,
    fromLocalDateString,
    monthDateFromLocalString,
} from '@/utils/dates'

import { BEST_BROADWAY_COLUMNS, CHEAP_TICKETS_COLUMNS } from './constants'

interface Props {
    numberOfColumns: number
    categoryImage: string | undefined
    production: Production
    shouldAddMoreDates: boolean
}

const ProductionCard: React.FC<Props> = ({ numberOfColumns, categoryImage, production, shouldAddMoreDates }) => {
    const { localDate, venue, name, minPrice, performers } = production
    const {
        selectors: { productionDetails },
    } = useContext(productionDetailsContext)

    const images = (productionDetails?.assets && productionDetails?.assets[0].images) ?? []
    const performer = performers[0]
    const externalImageTransformation = 't_production_card_image'
    const externalImagePath = images.find((image) => image.dataModelName === 'EXTERNAL')?.path
    const externalImage =
        externalImagePath &&
        `${process.env.NEXT_PUBLIC_EXTERNAL_IMAGE_URL_PREFIX}/${externalImageTransformation}${externalImagePath}`

    const mobileImage = images.find((image) => image.size === 'MOBILEHERO')?.path
    const image = mobileImage && normalizeSecureImages(`${mobileImage}?dimensions=400x200`)
    const getFooterContent = (): Record<string, string>[] => {
        const dateObject = fromLocalDateString(production.localDate)

        return numberOfColumns === BEST_BROADWAY_COLUMNS
            ? [
                  {
                      title: name,
                      subTitle: `${formatDateWithConditionalYear(dateObject)} ${
                          shouldAddMoreDates ? ' and more dates' : ''
                      }  •  ${venue.name}`,
                  },
              ]
            : [
                  {
                      title: monthDateFromLocalString(localDate),
                      subTitle: dateAndTimeFromLocalString(localDate),
                  },
                  { title: name, subTitle: venue.name },
              ]
    }

    const getBadgeContent = (): string | undefined => {
        return numberOfColumns === CHEAP_TICKETS_COLUMNS ? `from ${minPrice}` : undefined
    }

    return (
        <Link
            LinkComponent={NextLink}
            href={performer.webPath as string}
            importance="none"
            data-testid="product-card-link"
        >
            <CardWithImageAndFooter
                mobileImage={image || categoryImage}
                desktopImage={image || categoryImage}
                externalImage={externalImage}
                infoPillText={getBadgeContent()}
                footerContent={getFooterContent()}
            />
        </Link>
    )
}

export default ProductionCard
